import * as CONSTANTS from "./constants";
import * as UTILS from "./utils";

// All spektrix API queries will be handled by this method
const Spektrix_Request = async (url, verb = "GET", body = null, parser) => {
    if (!url) {
        throw new Error(`A URL must be provided to Spektrix_Request`);
    }

    let payload = {
        credentials: "include",
        headers: {
            "Content-type": "application/json; charset=UTF-8",
        },
        method: verb,
        body: body,
    };

    try {
        const response = await fetch(url, payload);

        // console.log("Spektrix_Request", response);

        if (!response.ok) {
            let errorData = null;
            try {
                errorData = await response.json();
            } catch (e) {
                // Response body wasn't valid JSON — fall back to a status message
            }
            const errorText = errorData?.problems?.join(". ");
            const message = errorText || errorData?.message || `Request failed with status ${response.status}`;
            const error = new Error(message);
            if (errorData?.errorCode !== undefined) error.errorCode = errorData.errorCode;
            if (errorData?.instanceLimits) error.instanceLimits = errorData.instanceLimits;
            throw error;
        }

        if (response.status != 204) {
            let data = null;
            if (parser) {
                const text = await response.text();
                data = parser(text);
            } else {
                data = await response.json();
            }

            return data;
        }

        if (response.status === 204) {
            return null;
        }
    } catch (error) {
        throw error;
    }
};

// Non spektrix API queries. These use ajax endpoints in the theme or spektrix plugin.
export const getEventImage = async (eventId) => {
    if (!eventId) return;

    let response = await fetch(CONSTANTS.AJAX_URL, {
        method: "POST",
        body: new URLSearchParams({ action: "event_image", event_id: eventId }),
    });
    let response_text = await response.text();
    return response_text;
};

export const getTopPicks = async (origin = "account", customer) => {
    let body = {
        action: "top_picks",
        origin: origin,
    };
    if (customer) body.customer = JSON.stringify(customer);

    let response = await fetch(CONSTANTS.AJAX_URL, {
        method: "POST",
        body: new URLSearchParams(body),
    });
    let response_text = await response.text();
    return response_text;
};

export const getTopPicksBasket = async (origin = "basket", customer) => {
    let body = {
        action: "top_picks",
        origin: origin,
    };
    if (customer) body.customer = JSON.stringify(customer);

    let response = await fetch(CONSTANTS.AJAX_URL, {
        method: "POST",
        body: new URLSearchParams(body),
    });
    let response_text = await response.text();
    return response_text;
};

// Spektrix API queries
export const getPlanData = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_PLUGIN_URL}/plans/${CONSTANTS.PLAN_ID}.json`,
        "GET",
    );
    return response;
};

export const getCustomPlanImageData = async () => {
    // Use a try/catch so this can fail silently if the file isn't there (which it usually won't be)
    try {
        const response = await fetch(
            `${CONSTANTS.SPEKTRIX_PLUGIN_URL}/custom-plan-images/${CONSTANTS.EVENT?.event_id}.json`,
        );

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return null;
    }
};

export const getSeatData = async () => {
    console.log("Fetching seat data...");

    // Make both requests in parallel
    const [proxyResponse, directResponse] = await Promise.all([
        fetch(`/?spektrix-detail&instance=${CONSTANTS.INSTANCE_ID}`, {
            method: "GET",
            credentials: "include",
            accept: "application/json",
            headers: {
                Origin: `${CONSTANTS.DOMAIN_NAME}`,
            },
        }).catch((err) => {
            console.error("Proxy request failed:", err);
            throw new Error("Proxy request failed");
        }),
        Spektrix_Request(
            `${CONSTANTS.SPEKTRIX_API_URL}/instances/${CONSTANTS.INSTANCE_ID}/status/detail?includeLockInformation=true&includeChildPlans=true&$expand=bands`,
            "GET",
            null,
            UTILS.parseSeatDetailObject,
        ).catch((err) => {
            console.error("Direct request failed:", err);
            throw new Error("Direct request failed");
        }),
    ]).catch((err) => {
        console.error("Error in Promise.all:", err);
        throw new Error("Failed to fetch seat data");
    });

    // Process the responses
    const proxyText = await proxyResponse.text().catch((err) => {
        console.error("Failed to read proxy response text:", err);
        throw new Error("Failed to read proxy response text");
    });

    const proxyData = JSON.parse(proxyText);
    const maskedSeatIds = proxyData?.data?.response || {};

    const mergedData = directResponse.concat(maskedSeatIds);
    // console.log("Merged Data:", mergedData);

    return UTILS.parseSeatStatus(mergedData);
};

export const getAreaData = async (areaId) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/instances/${CONSTANTS.INSTANCE_ID}/status/areas?areaIds=${areaId}&includeLockInformation=true&includeChildPlans=true&expand=2`,
        "GET",
    );

    return response;
};

export const addBestAvailable = async (quantity, priceBands = null) => {
    let body = {
        Quantity: quantity,
        Instance: CONSTANTS.INSTANCE_ID,
    };
    if (priceBands) {
        body.priceBands = priceBands;
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/tickets/best-available`,
        "POST",
        JSON.stringify(body),
    );

    return response;
};

export const getBestAvailableByBands = async (quantity, priceBands = null) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/instances/${CONSTANTS.INSTANCE_ID}/best-available?quantity=${quantity}&bandIds=${priceBands}`,
        "GET",
    );

    return response;
};

export const addTickets = async (
    quantity = 1,
    ticketType = "",
    seat_id = "",
    instance_id = CONSTANTS.INSTANCE_ID,
    planId = CONSTANTS.PLAN_ID,
) => {
    const body = {
        Instance: instance_id,
        Type: ticketType,
    };

    // Reserved tickets require a seat id to be passed in unreserved require the seating plan id
    if (seat_id) {
        body.Seat = seat_id;
    } else {
        body.SeatingPlan = planId;
    }

    // Add a data object to the body for each required ticket
    let body_arr = [];
    for (let i = 0; i < quantity; i++) {
        body_arr.push(body);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/tickets`,
        "POST",
        JSON.stringify(body_arr),
    );
    return response;
};

export const removeTickets = async (ticketIds = "") => {
    if (!ticketIds) {
        throwError({
            ok: false,
            statusText: "removeTickets() requires ticketIds",
        });
    }

    let response = await Spektrix_Request(
        `${
            CONSTANTS.SPEKTRIX_API_URL
        }/basket/tickets?ticketIds=${ticketIds.join(",")}`,
        "DELETE",
    );
    return response;
};

export const patchTicket = async (ticketId = null, body = null) => {
    if (!ticketId) {
        throwError({
            ok: false,
            statusText: "patchTickets() requires ticketId",
        });
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/tickets/${ticketId}`,
        "PATCH",
        JSON.stringify(body),
    );
    return response;
};

export const getFunds = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/funds`,
        "GET",
    );
    return response;
};

export const getTicketTypes = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/ticket-types`,
        "GET",
    );
    return response;
};

export const getBands = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/bands`,
        "GET",
    );
    return response;
};

export const getInstance = async (instanceId = CONSTANTS.INSTANCE_ID) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/instances/${instanceId}?$expand=1`,
        "GET",
    );
    return response;
};

export const getInstanceStatus = async (instanceId = CONSTANTS.INSTANCE_ID) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/instances/${instanceId}/status`,
        "GET",
    );
    return response;
};

export const getInstancePriceList = async (
    instanceId = CONSTANTS.INSTANCE_ID,
) => {
    let response = await fetch(`/?spektrix-prices&instance=${instanceId}`, {
        method: "GET",
        credentials: "include",
        headers: {
            "Content-type": "application/json; charset=UTF-8",
        },
    });
    let data = await response.json();

    // Add commission to price
    const priceListData = data.prices.map((price) => {
        price.customItems?.forEach((item) => {
            if (item.name === "Commission") {
                price.amount =
                    parseFloat(price.amount) + parseFloat(item.amount);
            }
        });

        return price;
    });

    return priceListData;
};

export const getSupplementaryEvents = async (
    instanceId = CONSTANTS.INSTANCE_ID,
) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/instances/${instanceId}/supplementary-events?$expand=event,priceList`,
        "GET",
    );

    return response;
};

export const getEvent = async (eventId = CONSTANTS.EVENT.event_id) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/events/${eventId}?$expand=1`,
        "GET",
    );
    return response;
};

export const getEventInstances = async (eventId = CONSTANTS.EVENT.event_id) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/events/${eventId}/instances?interface=1`,
        "GET",
    );
    return response;
};

export const patchTicketType = async (ticketId, ticketType) => {
    if (!ticketId) {
        throwError({
            ok: false,
            statusText: "patchTicketType() requires ticketId",
        });
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/tickets/${ticketId}`,
        "PATCH",
        JSON.stringify({
            TicketType: ticketType,
        }),
    );
    return response;
};

export const getBasket = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket?$expand=tickets/instance,tickets/event,tickets/seat,tickets/ticketType,tickets/band,donations,membershipSubscriptions/membership,giftVouchers,merchandiseItems,customer/subscriptions,ticketDelivery,ticketDeliveryOptions`,
        "GET",
    );
    return response;
};

export const patchBasket = async (body) => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket`,
        "PATCH",
        JSON.stringify(body),
    );
    return response;
};

export const getBasketTickets = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/tickets?$expand=1`,
        "GET",
    );
    return response;
};

export const clearBasket = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/clear`,
        "GET",
    );
    return response;
};

export const getDonationPrompts = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/donations-to-prompt`,
        "GET",
    );
    return response;
};

export const getCustomer = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer?$expand=tags,subscriptions/membership,AllStatements`,
        "GET",
    );
    return response;
};

export const patchCustomerDetails = async (details) => {
    console.log("details", details);

    if (!details) return;
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer`,
        "PATCH",
        JSON.stringify(details),
    );
    return response;
};

export const getCustomerAddresses = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/addresses`,
        "GET",
    );
    return response;
};

export const patchCustomerAddresses = async (addresses) => {
    if (!addresses) return;
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/addresses`,
        "PATCH",
    );
    return response;
};

export const logOut = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/deauthenticate`,
        "POST",
    );
    return response;
};

export const getAddressesFromPostcode = async (postcode) => {
    if (!postcode) {
        throw new Error(`No postcode was provided`);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/addresses?postcode=${postcode}`,
        "GET",
    );
    return response;
};

export const getAddressFromPostcodeId = async (id) => {
    if (!id) {
        throw new Error(`No id was provided`);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/addresses/${id}`,
        "GET",
    );
    return response;
};

export const getCountries = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/countries`,
        "GET",
    );
    return response;
};

export const getStatements = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/statements`,
        "GET",
    );
    return response;
};

export const createCustomer = async (customer) => {
    if (!customer) {
        throw new Error(`No customer data was provided`);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/?domain=${CONSTANTS.DOMAIN_NAME}`,
        "POST",
        JSON.stringify(customer),
    );
    return response;
};

export const addContactPreference = async (body) => {
    if (!body) {
        throw new Error(`No body was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/agreed-statements`,
        "POST",
        JSON.stringify(body),
    );
    return response;
};

export const removeContactPreference = async (statementId) => {
    if (!statementId) {
        throw new Error(`No statement ID was provided`);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/agreed-statements/${statementId}`,
        "DELETE",
    );
    return response;
};

export const replaceContactPreferences = async (body) => {
    if (!body) {
        throw new Error(`No body was provided`);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/agreed-statements`,
        "PUT",
        JSON.stringify(body),
    );
    return response;
};

export const addCustomerTag = async (body) => {
    if (!body) {
        throw new Error(`No body was provided to addCustomerTag()`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/tags`,
        "POST",
        JSON.stringify(body),
    );
    return response;
};

export const removeCustomerTag = async (tagId) => {
    if (!tagId) {
        throw new Error(`No statement ID was provided`);
    }

    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/tags/${tagId}`,
        "DELETE",
    );
    return response;
};

export const addMembership = async (body) => {
    if (!body) {
        throw new Error(`No body was provided to addMembership()`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/membership-subscriptions`,
        "POST",
        JSON.stringify(body),
    );
    return response;
};

export const getPotentialDiscount = async (membershipId) => {
    if (!membershipId) {
        throw new Error(`No membership id was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/potentialdiscount?membershipId=${membershipId}`,
        "GET",
    );
    return response;
};

export const addDonation = async (body) => {
    if (!body) {
        throw new Error(`No body was provided to addDonationToBasket()`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/donations`,
        "POST",
        JSON.stringify(body),
    );
    return response;
};

export const removeDonation = async (donationId) => {
    if (!donationId) {
        throw new Error(`No donationId was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/donations/${donationId}`,
        "DELETE",
    );
    return response;
};

export const patchDonation = async (donationId, body = null) => {
    if (!donationId) {
        throw new Error(`No donationId was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/donations/${donationId}`,
        "PATCH",
        JSON.stringify(body),
    );
    return response;
};

export const getOrders = async () => {
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/customer/orders?$expand=donations/fund,refunds,membershipsubscriptions/membership,tickets/instance/event,tickets/ticketType,tickets/seat`,
        "GET",
    );
    return response;
};

export const getAuthenticatedOrders = async (
    customerId,
    dateFrom,
    dateTo,
    upcoming,
) => {
    const start = performance.now();
    try {
        const response = await fetch(
            `/?spektrix-customer-orders&customer-id=${customerId}&date-from=${dateFrom}&date-to=${dateTo}&upcoming-only=${upcoming}`,
        );

        if (!response.ok) {
            throw new Error("Failed to fetch authenticated orders");
        }

        return await response.json();
    } catch (error) {
        throw error;
    } finally {
        if (upcoming) {
            const end = performance.now();
            window.dataLayer.push({
                event: "upcomingOrdersFetchTime",
                upcomingOrdersFetchDuration: Math.round(end - start),
            });
        }
    }
};

export const removeMembership = async (subscriptionId) => {
    if (!subscriptionId) {
        throw new Error(`No subscriptionId was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/membership-subscriptions/${subscriptionId}`,
        "DELETE",
    );
    return response;
};

export const patchMembership = async (subscriptionId, body) => {
    if (!subscriptionId) {
        throw new Error(`No subscriptionId was provided`);
    }
    if (!body) {
        throw new Error(`No body was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/membership-subscriptions/${subscriptionId}`,
        "PATCH",
        JSON.stringify(body),
    );
    return response;
};

export const removeGiftVoucher = async (giftVoucherId) => {
    if (!giftVoucherId) {
        throw new Error(`No giftVoucherId was provided`);
    }
    let response = await Spektrix_Request(
        `${CONSTANTS.SPEKTRIX_API_URL}/basket/gift-vouchers/${giftVoucherId}`,
        "DELETE",
    );
    return response;
};
